import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { IJob, getCandidateResumeByFileUrl, getPresignedURLForGet } from 'wisestep-api';
import { Item, WsColors, WsTypography, getSearchRegExp } from 'wisestep-base';

import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { filterS3Url, trackCV } from '../utils/cvHelper';

import PdfViewer from './PdfViewer';

const IFrame = styled('iframe')(() => ({
  height: '100%',
  width: '100%',
  border: 0,
  overflowY: 'auto',
}));

interface IConversationResumeView {
  resumeLocation?: string;
  isOriginaFormatView?: boolean;
  pdfResumeUrl?: string;
  job: IJob | null;
}

const ConversationResumeView: FC<IConversationResumeView> = ({
  resumeLocation,
  isOriginaFormatView,
  pdfResumeUrl,
  job,
}) => {
  const [isResumeLoaded, setIsResumeLoaded] = useState(false);
  const [url, setUrl] = useState<{ url: string; type: string; resumeContent?: string } | null>(
    null
  );
  const searchQuery = useConversationWidgetStore((state) => state.searchQuery);
  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);
  const [regex] = useState<RegExp | undefined>(getSearchRegExp(searchQuery));

  useEffect(() => {
    const getHighLightKeyWordResume = async (url: string) => {
      if (searchQuery && !isOriginaFormatView) {
        try {
          const response = await getCandidateResumeByFileUrl(url);
          if (
            response.data.content &&
            response.data.content !== 'content not found' &&
            response.data.content !== 'NO CONTENT' &&
            response.data.content !== 'NO_CONTENT' &&
            response.data.content !== 'EXTRACTION_FAILED'
          ) {
            return {
              url: response.data.resumeLocation ? response.data.resumeLocation : '',
              type: 'content',
              resumeContent: response.data.content.replace(/\n/g, '<br>'),
            };
          }
          return await filterS3Url(url);
        } catch (error) {
          return await filterS3Url(url);
        }
      }
      return await filterS3Url(url);
    };
    (async () => {
      if (!isEmpty(resumeLocation)) {
        setIsResumeLoaded(false);
        const getURL = await getHighLightKeyWordResume(resumeLocation ?? '');
        setUrl(getURL);
        setIsResumeLoaded(true);
        job == null
          ? trackCV('view', resumeLocation, cvViewSourcePage)
          : trackCV('view', resumeLocation, cvViewSourcePage, job);
      } else {
        setIsResumeLoaded(true);
      }
    })();
  }, [job, resumeLocation, searchQuery, isOriginaFormatView, cvViewSourcePage]);
  const [resumeUrl, setResumeUrl] = useState('');
  useEffect(() => {
    const getUrl = async () => {
      const data = await getPresignedURLForGet({
        fileName: resumeLocation ?? '',
      });
      setResumeUrl(data.data.preSignedUrl);
    };
    getUrl();
  }, [resumeLocation]);

  return (
    <Item sx={{ height: '100%', bgcolor: WsColors.White, marginRight: 1 }}>
      {isResumeLoaded ? (
        <>
          {url ? (
            isOriginaFormatView ? (
              <>
                {url.type == 'image' ? (
                  <img
                    id="imageviewer"
                    src={url.url}
                    style={{ width: '100%' }}
                    alt="CV View Error"
                  />
                ) : resumeLocation?.split('.').pop() === 'pdf' ? (
                  <PdfViewer url={resumeUrl} highlightRegexp={regex} />
                ) : pdfResumeUrl &&
                  (resumeLocation?.split('.').pop() === 'doc' ||
                    resumeLocation?.split('.').pop() === 'docx') ? (
                  <IFrame src={pdfResumeUrl + '#toolbar=0'} />
                ) : (
                  <IFrame src={url.url} />
                )}
              </>
            ) : (
              <>
                {url.type == 'image' ? (
                  <img
                    id="imageviewer"
                    src={url.url}
                    style={{ width: '100%' }}
                    alt="CV View Error"
                  />
                ) : url.type == 'content' ? (
                  <Box sx={{ height: '100%', bgcolor: WsColors.White, marginRight: 1 }}>
                    <Typography
                      height="100%"
                      sx={{ overflow: 'hidden', overflowY: 'scroll' }}
                      dangerouslySetInnerHTML={{
                        __html: url.resumeContent
                          ? highlightKeys(
                              url.resumeContent.replace(
                                /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
                                'illegalscript'
                              ),
                              searchQuery ?? ''
                            )
                          : '',
                      }}
                    />
                  </Box>
                ) : (
                  <IFrame id="imageviewer" title="imageviewer" src={url.url} />
                )}
              </>
            )
          ) : (
            <Stack direction="row" justifyContent="center" alignItems="center">
              <WsTypography>
                {resumeLocation && resumeLocation.indexOf('.doc') > -1
                  ? '.doc format file view are not supported in browser. Please download the file to view it locally'
                  : 'CV Not Available'}
              </WsTypography>
            </Stack>
          )}
        </>
      ) : (
        <Stack direction="row" justifyContent="center" alignItems="center">
          <WsTypography>Loading CV...</WsTypography>
        </Stack>
      )}
    </Item>
  );
};

export default ConversationResumeView;

export const highlightKeys = (textToHighlight: string, keywords: string) => {
  let filteredText = textToHighlight;
  if (keywords) {
    const pattern = getSearchRegExp(keywords);
    filteredText = textToHighlight.replaceAll(pattern ?? new RegExp(''), '<em>$2</em>');
  }
  return filteredText;
};
